import { Product } from '../../../services/api/domains/products/types';
import { FieldConfig } from '../ModalFieldRenderer';
import { formatDateTime } from 'utils/dateConversion';
import { ManufacturerSummary } from 'services/api/domains/facilities/types';
import { FacilitySummary } from 'services/api/domains/manufacturers/types';
import { Grade } from 'services/api/domains/grades/types';
import { TherapeuticArea } from 'services/api/domains/therapeutic-areas/types';
import { stripHtmlTags } from 'utils/renderHtml';

export type ProductFieldOptions = {
  manufacturers?: ManufacturerSummary[];
  facilities?: FacilitySummary[];
  grades?: Grade[];
  therapeuticAreas?: TherapeuticArea[];
};

export const getProductFields = (
  opts: ProductFieldOptions = {}
): FieldConfig<Product>[] => [
  // 1. Basic product information - Product Name
  {
    key: 'product_name',
    label: 'Product Name',
    type: 'text',
    getValue: p => stripHtmlTags(p.product_name || ''),
    setValue: (p, val) => ({ ...p, product_name: String(val || '') }),
    isRequired: true,
  },

  // 2. Preferred Product Name with Synonyms Radio Selection
  {
    key: 'preferred_product_name' as keyof Product,
    label: 'Preferred Product Name',
    type: 'synonyms_radio' as const,
    getValue: p => ({
      preferredValue: p.preferred_product_name || '',
      synonyms: p.synonyms || [],
    }),
    setValue: (p, val) => ({
      ...p,
      preferred_product_name: val || null,
    }),
    tooltipText:
      'The Product Name is sometimes set as the official CAS Registry name (often a chemical formula). Use Preferred Product Name to set a simpler, common name for internal reference. Select from synonyms below or enter a custom name.',
  },

  // 3. Product Types (Grades)
  {
    key: 'grades',
    label: 'Product Type',
    type: 'multiselect',
    getValue: p =>
      Array.isArray(p.grades)
        ? p.grades.map(g => g.grade_id?.toString() || '')
        : [],
    setValue: (p, val) => ({
      ...p,
      grades: Array.isArray(val)
        ? val.map(id => {
            const grade = opts.grades?.find(g => g.grade_id?.toString() === id);
            return grade
              ? {
                  grade_id: grade.grade_id,
                  grade_name: grade.grade_name,
                }
              : { grade_id: parseInt(id, 10), grade_name: '' };
          })
        : [],
    }),
    options:
      opts.grades?.map(g => ({
        value: g.grade_id?.toString() || '',
        label: g.grade_name || '',
      })) || [],
    isRequired: false,
  },

  // 4. Therapeutic Areas
  {
    key: 'therapeutic_areas',
    label: 'Therapeutic Area',
    type: 'multiselect',
    getValue: p =>
      Array.isArray(p.therapeutic_areas)
        ? p.therapeutic_areas.map(
            ta => ta.therapeutic_area_id?.toString() || ''
          )
        : [],
    setValue: (p, val) => ({
      ...p,
      therapeutic_areas: Array.isArray(val)
        ? val.map(id => {
            const area = opts.therapeuticAreas?.find(
              ta => ta.therapeutic_area_id?.toString() === id
            );
            return area
              ? {
                  therapeutic_area_id: area.therapeutic_area_id,
                  therapeutic_area_name: area.therapeutic_area_name,
                }
              : {
                  therapeutic_area_id: parseInt(id, 10),
                  therapeutic_area_name: '',
                };
          })
        : [],
    }),
    options:
      opts.therapeuticAreas?.map(ta => ({
        value: ta.therapeutic_area_id?.toString() || '',
        label: ta.therapeutic_area_name || '',
      })) || [],
    isRequired: false,
  },

  // 5. Product details - CAS Number
  {
    key: 'cas_number',
    label: 'CAS #',
    type: 'text',
    getValue: p => p.cas_number || '',
    setValue: (p, val) => ({ ...p, cas_number: val ? String(val) : undefined }),
  },

  // 6. Manufacturers (with manufacturing status)
  {
    key: 'manufacturers',
    label: 'Associated Manufacturers',
    type: 'list_builder',
    getValue: p => {
      return Array.isArray(p.manufacturers)
        ? p.manufacturers.map(m => ({
            id: m.manufacturer_id,
            label: m.manufacturer_name,
            status: m.manufacturing_status || null,
          }))
        : [];
    },
    setValue: (p, val) => {
      const manufacturers = Array.isArray(val)
        ? val.map(item => {
            const manufacturer = opts.manufacturers?.find(
              m => m.manufacturer_id === item.id
            );
            return {
              manufacturer_id: item.id,
              manufacturer_name: manufacturer?.manufacturer_name || item.label,
              manufacturing_status: item.status || null,
            };
          })
        : [];
      return {
        ...p,
        manufacturers,
      };
    },
    availableItems:
      opts.manufacturers?.map(m => ({
        id: m.manufacturer_id,
        label: m.manufacturer_name,
      })) || [],
    listBuilderConfig: {
      idKey: 'Manufacturer',
      labelKey: 'Manufacturer',
      statusKey: 'Status',
      statusOptions: [
        { value: 'Commercial', label: 'Commercial' },
        { value: 'R&D', label: 'R&D' },
      ],
      addButtonText: 'Add Manufacturer',
      addModalTitle: 'Add Manufacturers',
      noItemsText: 'No manufacturers associated with this product',
    },
    isRequired: false,
  },

  // 7. Facilities
  {
    key: 'facilities',
    label: 'Facilities',
    type: 'multiselect',
    getValue: p =>
      Array.isArray(p.facilities)
        ? p.facilities.map(f => f.facility_id?.toString() || '')
        : [],
    setValue: (p, val) => ({
      ...p,
      facilities: Array.isArray(val)
        ? val.map(id => {
            const facility = opts.facilities?.find(
              f => f.facility_id?.toString() === id
            );
            return facility
              ? {
                  facility_id: facility.facility_id,
                  facility_name: facility.facility_name,
                }
              : { facility_id: parseInt(id, 10), facility_name: '' };
          })
        : [],
    }),
    options:
      opts.facilities?.map(f => ({
        value: f.facility_id?.toString() || '',
        label: f.facility_name || '',
      })) || [],
    isRequired: false,
  },

  // 8. Notes field
  {
    key: 'note',
    label: 'Note',
    type: 'textarea',
    getValue: p => p.note || '',
    setValue: (p, val) => ({ ...p, note: val ? String(val) : undefined }),
    rows: 4,
  },

  // 9. Timestamp fields (readonly)
  {
    key: 'created_date',
    label: 'Created',
    type: 'readonly',
    getValue: p => p.created_date || '',
    formatter: formatDateTime,
    isAlwaysDisabled: true,
  },
  {
    key: 'modified_date',
    label: 'Last Updated',
    type: 'readonly',
    getValue: p => p.modified_date || '',
    formatter: formatDateTime,
    isAlwaysDisabled: true,
  },
  {
    key: 'user_id',
    label: 'User',
    type: 'text',
    getValue: p => p.user_id || '',
    isAlwaysDisabled: true,
  },
];

// Always disabled field names for products
export const productAlwaysDisabledFields = [
  'manufacturer_name',
  'synonyms',
  'facility_name',
  'grade_name',
  'therapeutic_area_name',
  'created_date',
  'modified_date',
  'user_id',
];
